/**
 * nubee-gravity-forms-dropzone.js
 * Replaces the Nth `.gform_fileupload_multifile` node with a `.dropzone`,
 * validates client-side (extensions/size/count), toggles `.invalid-upload`,
 * and posts form_id + field_id.
 *
 * ID pattern honored: customDropzone-<formId>-<indexWithinForm> (1-based).
 * Back-compat: customDropzone-<indexWithinForm> still works.
 *
 * Changes in this canvas revision:
 * - Rules fallback via DOM parsing of `.gform_fileupload_rules` (FR/EN).
 * - Limit error shows **once per batch**, not for every invalid file.
 * - Invalid files are **NOT stored** in the session map (so later uploads
 *   don’t count them). Error items auto-remove after a timeout.
 */

// Public API
export function nubeeGFDropzone( formId, dropzoneId, rules = {}, fieldId = null ) {
	const formEl = document.getElementById( 'gform_' + formId );
	if( ! formEl ) {
		console.error( 'GF form not found for id:', formId );
		return;
	}

	// Parse index from dropzoneId
	let match = /^customDropzone-(\d+)-(\d+)$/.exec( dropzoneId );
	let idx = null;
	if( match ) {
		const idForm = parseInt( match[1], 10 );
		if( idForm !== parseInt( formId, 10 ) ) {
			console.warn( 'dropzoneId formId mismatch; using function arg formId:', formId, 'id says:', idForm );
		}
		idx = parseInt( match[2], 10 ) - 1;
	} else {
		match = /^customDropzone-(\d+)$/.exec( dropzoneId );
		if( match ) idx = parseInt( match[1], 10 ) - 1;
	}
	if( idx === null || isNaN( idx ) || idx < 0 ) idx = 0;

	const multiNodes = formEl.querySelectorAll( '.gform_fileupload_multifile' );
	if( ! multiNodes.length ) {
		console.error( 'No .gform_fileupload_multifile found in form', formId );
		return;
	}
	const existing = multiNodes[ idx ] || multiNodes[0];

	// Resolve container & fieldId & rules
	const container = existing.closest( '.ginput_container_fileupload' );
	if( fieldId === null && container ) {
		const fidAttr = container.getAttribute( 'data-field-id' );
		if( fidAttr ) fieldId = parseInt( fidAttr, 10 );
	}
	const resolvedRules = Object.assign(
		{ extensions: [], maxFiles: null, maxSizeMB: null },
		readRulesFor( formId, fieldId, container ),
		rules || {}
	);

	const dropzone = buildDropzoneElement( dropzoneId );
	existing.parentNode.replaceChild( dropzone, existing );
	wireDropzone( dropzone, formId, fieldId, resolvedRules );
}

// Auto-init for all fileupload fields on the page
(function() {
	if( document.readyState === 'loading' ) {
		document.addEventListener( 'DOMContentLoaded', initAllGFFileUploads );
	} else {
		initAllGFFileUploads();
	}
})();

function initAllGFFileUploads() {
	document.querySelectorAll( '.gform_wrapper form[id^="gform_"]' ).forEach( ( formEl ) => {
		const formId = parseInt( ( formEl.id || '' ).replace( 'gform_', '' ), 10 );
		if( ! formId ) return;

		const multiNodes = formEl.querySelectorAll( '.gform_fileupload_multifile' );
		multiNodes.forEach( ( existing, i ) => {
			const idx1 = i + 1;
			const dropId = 'customDropzone-' + formId + '-' + idx1;

			const container = existing.closest( '.ginput_container_fileupload' );
			let fieldId = container && container.getAttribute( 'data-field-id' )
				? parseInt( container.getAttribute( 'data-field-id' ), 10 )
				: null;
			const resolvedRules = readRulesFor( formId, fieldId, container );

			const dropzone = buildDropzoneElement( dropId );
			existing.parentNode.replaceChild( dropzone, existing );
			wireDropzone( dropzone, formId, fieldId, resolvedRules );
		} );
	} );
}

function readRulesFor( formId, fieldId, container ) {
	// Priority 1: rules pushed by PHP
	if(
		window.nubeeGF &&
		window.nubeeGF.rules &&
		window.nubeeGF.rules[ formId ] &&
		fieldId !== null &&
		window.nubeeGF.rules[ formId ][ fieldId ]
	) {
		return window.nubeeGF.rules[ formId ][ fieldId ];
	}

	// Priority 2: data-* on container
	if( container ) {
		const extsAttr = container.getAttribute( 'data-allowed-ext' );
		const maxFilesAttr = container.getAttribute( 'data-max-files' );
		const maxSizeAttr = container.getAttribute( 'data-max-sizemb' );
		if( extsAttr || maxFilesAttr || maxSizeAttr ) {
			const exts = ( extsAttr || '' ).split( ',' ).map( s => s.trim().toLowerCase() ).filter( Boolean );
			return {
				extensions: exts,
				maxFiles: maxFilesAttr ? parseInt( maxFilesAttr, 10 ) : null,
				maxSizeMB: maxSizeAttr ? parseInt( maxSizeAttr, 10 ) : null
			};
		}
	}

	// Priority 3: parse `.gform_fileupload_rules` text (FR/EN)
	let extensions = [];
	let maxFiles = null;
	let maxSizeMB = null;

	if( container ) {
		const rulesEl =
			container.querySelector( '.gform_fileupload_rules' ) ||
			( container.parentElement && container.parentElement.querySelector( '.gform_fileupload_rules' ) );
		if( rulesEl && rulesEl.textContent ) {
			const txt = rulesEl.textContent;

			// Extensions
			let mExt = /types?\s+de\s+fichiers\s+acceptés\s*[:：]\s*([a-z0-9,\s\.\-]+)/i.exec( txt );
			if( ! mExt ) mExt = /allowed\s+file\s+types?\s*[:：]\s*([a-z0-9,\s\.\-]+)/i.exec( txt );
			if( mExt && mExt[1] ) {
				extensions = mExt[1]
					.split( /[, ]+/ )
					.map( s => s.replace( /^\./, '' ).trim().toLowerCase() )
					.filter( Boolean );
			} else {
				const listMatch = txt.toLowerCase().match( /([a-z0-9]+(?:\s*,\s*[a-z0-9]+)+)/i );
				if( listMatch ) {
					extensions = listMatch[1]
						.split( ',' )
						.map( s => s.trim().toLowerCase() )
						.filter( Boolean );
				}
			}

			// Max size (e.g. 10 MB / 1 GB)
			let mSize = /(taille\s*max\.?\s*des\s*fichiers|max(?:imum)?\s*file\s*size)\s*[:：]\s*([0-9]+)\s*([mg]b?)/i.exec( txt );
			if( mSize ) {
				const n = parseInt( mSize[2], 10 );
				const unit = ( mSize[3] || 'mb' ).toLowerCase();
				if( ! isNaN( n ) && n > 0 ) maxSizeMB = unit.startsWith( 'g' ) ? n * 1024 : n;
			}

			// Max files
			let mCount = /(max(?:\.|imum)?\s*(?:des\s*)?fichiers|maximum\s*files?)\s*[:：]\s*([0-9]+)/i.exec( txt );
			if( mCount ) {
				const n = parseInt( mCount[2], 10 );
				if( ! isNaN( n ) && n > 0 ) maxFiles = n;
			}
		}
	}

	return { extensions, maxFiles, maxSizeMB };
}

function buildDropzoneElement( dropzoneId ) {
	const dz = document.createElement( 'div' );
	dz.id = dropzoneId;
	dz.className = 'dropzone';

	const instructions = document.createElement( 'div' );
	instructions.className = 'instructions';

	const label = document.createElement( 'span' );
	label.className = 'label';
	label.innerText = nubeeGlobals?.i18n?.dropzone?.dropzoneLabel || 'Drop or select files.';

	const fakeBtn = document.createElement( 'span' );
	fakeBtn.className = 'fake-button button white';
	fakeBtn.innerText = nubeeGlobals?.i18n?.dropzone?.button || 'Browse';

	instructions.appendChild( label );
	instructions.appendChild( fakeBtn );
	dz.appendChild( instructions );

	const ul = document.createElement( 'ul' );
	ul.className = 'files-list';
	dz.appendChild( ul );

	const bar = document.createElement( 'div' );
	bar.className = 'progress-bar';
	bar.style.width = '0%';
	bar.style.opacity = '0';
	dz.appendChild( bar );

	return dz;
}

function wireDropzone( dropzone, formId, fieldId, rules ) {
	const fileList = dropzone.querySelector( '.files-list' );
	const progressBar = dropzone.querySelector( '.progress-bar' );
	const instructions = dropzone.querySelector( '.instructions' );

	const uploadedFiles = new Map(); // key: name|size => File
	const ERROR_TTL_MS = 3500; // auto-remove error messages after 3.5s

	function setInvalid( on ) {
		if( on ) dropzone.classList.add( 'invalid-upload' );
		else dropzone.classList.remove( 'invalid-upload' );
	}

	function currentValidCount() {
		let c = 0;
		uploadedFiles.forEach( f => { if( ! f.__invalid ) c++; } );
		return c;
	}

	function addErrorItem( messages ) {
		const li = document.createElement( 'li' );
		li.className = 'file error';
		li.innerText = messages.join( ' ' );
		fileList.appendChild( li );
		setInvalid( true );
		// auto remove after TTL
		setTimeout( () => {
			if( li.parentNode ) li.parentNode.removeChild( li );
			if( ! fileList.querySelector( '.file.error' ) ) setInvalid( false );
		}, ERROR_TTL_MS );
	}

	// Click to open selector
	instructions.addEventListener( 'click', () => {
		const input = document.createElement( 'input' );
		input.type = 'file';
		input.multiple = true;
		input.style.display = 'none';

		if( Array.isArray( rules.extensions ) && rules.extensions.length ) {
			input.accept = rules.extensions.map( e => '.' + e ).join( ',' );
		}

		input.addEventListener( 'change', ( e ) => handleFiles( e.target.files ) );
		document.body.appendChild( input );
		input.click();
		document.body.removeChild( input );
	} );

	// Drag & drop
	dropzone.addEventListener( 'dragover', ( e ) => {
		e.preventDefault();
		dropzone.classList.add( 'drag-over' );
	} );
	dropzone.addEventListener( 'dragleave', () => {
		dropzone.classList.remove( 'drag-over' );
	} );
	dropzone.addEventListener( 'drop', ( e ) => {
		e.preventDefault();
		dropzone.classList.remove( 'drag-over' );
		handleFiles( e.dataTransfer.files );
	} );

	function validateFile( file, willBeNth ) {
		const errs = [];
		const ext = ( file.name.split( '.' ).pop() || '' ).toLowerCase();

		if( Array.isArray( rules.extensions ) && rules.extensions.length && ! rules.extensions.includes( ext ) ) {
			errs.push( 'type' );
		}
		if( Number.isFinite( rules.maxSizeMB ) && rules.maxSizeMB > 0 ) {
			const maxBytes = rules.maxSizeMB * 1024 * 1024;
			if( file.size > maxBytes ) errs.push( 'size' );
		}
		if( Number.isFinite( rules.maxFiles ) && rules.maxFiles > 0 ) {
			// willBeNth = currentValid + 1 if accepted
			if( willBeNth > rules.maxFiles ) errs.push( 'limit' );
		}
		return errs;
	}

	function handleFiles( files ) {
		let hasInvalid = false;
		let limitShown = false; // show limit error once per batch
		const validToUpload = [];

		Array.from( files ).forEach( ( file ) => {
			const key = file.name + '|' + file.size;
			if( uploadedFiles.has( key ) ) return; // already present

			const willBeNth = currentValidCount() + 1;
			const errs = validateFile( file, willBeNth );

			if( errs.length ) {
				hasInvalid = true;

				const msgs = [];
				if( errs.includes( 'type' ) ) msgs.push( nubeeGlobals?.i18n?.dropzone?.invalidType || 'Invalid file type.' );
				if( errs.includes( 'size' ) ) msgs.push( nubeeGlobals?.i18n?.dropzone?.invalidSize || 'File too large.' );
				if( errs.includes( 'limit' ) && ! limitShown ) {
					msgs.push( nubeeGlobals?.i18n?.dropzone?.invalidLimit || 'File limit reached.' );
					limitShown = true;
				}
				if( msgs.length ) addErrorItem( msgs );
				return; // DO NOT store invalid files in the map
			}

			// Store valid file only
			uploadedFiles.set( key, file );

			const li = document.createElement( 'li' );
			li.className = 'file';

			const nameSpan = document.createElement( 'span' );
			nameSpan.className = 'file-name';
			nameSpan.innerText = file.name;

			const removeBtn = document.createElement( 'button' );
			removeBtn.className = 'file-remove-btn';
			removeBtn.type = 'button';
			removeBtn.innerText = nubeeGlobals?.i18n?.dropzone?.remove || 'Remove';
			removeBtn.addEventListener( 'click', () => {
				uploadedFiles.delete( key );
				fileList.removeChild( li );
				if( ! fileList.querySelector( '.file.error' ) ) setInvalid( false );
			} );

			li.appendChild( nameSpan );
			li.appendChild( removeBtn );
			fileList.appendChild( li );

			validToUpload.push( file );
		} );

		if( validToUpload.length === 0 ) return;
		if( hasInvalid ) return; // don’t upload automatically if any invalid in batch
		uploadFiles();
	}

	function uploadFiles() {
		if( uploadedFiles.size === 0 ) return;

		const formData = new FormData();
		let i = 0;
		for( const f of uploadedFiles.values() ) {
			formData.append( 'file' + ( i++ ), f );
		}
		if( i === 0 ) return;

		formData.append( 'upload_nonce', nubeeGlobals?.dropzoneNonce || '' );
		formData.append( 'form_id', String( formId ) );
		if( fieldId !== null ) formData.append( 'field_id', String( fieldId ) );

		const xhr = new XMLHttpRequest();
		xhr.open( 'POST', nubeeGlobals?.uploadUrl || '', true );

		xhr.upload.onprogress = ( event ) => {
			if( event.lengthComputable ) {
				const percent = ( event.loaded / event.total ) * 100;
				progressBar.style.opacity = '1';
				progressBar.style.width = percent + '%';
			}
		};

		xhr.onload = () => {
			if( xhr.status === 200 ) {
				progressBar.style.opacity = '0';
				progressBar.style.width = '100%';
				if( ! dropzone.querySelector( '.file.error' ) ) setInvalid( false );
			} else {
				console.error( 'Upload failed with status', xhr.status );
			}
		};

		xhr.onerror = () => console.error( 'Upload network error' );
		xhr.send( formData );
	}
}
